import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError, buildWhatsappUrl, track } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function InquiryDialog({ children, defaultMessage = "", source = "inquiry_form", sourceRef, triggerClassName, triggerTestId = "open-inquiry-btn" }) {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", whatsapp: "", email: "", city: "",
    travel_date: "", travellers: "", message: defaultMessage,
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWaMessage = (leadCode) => {
    const lines = [
      `Hi! I've just submitted a booking on your website.`,
      leadCode ? `Lead ID: ${leadCode}` : null,
      `Name: ${form.name}`,
      `Mobile: ${form.mobile}`,
      form.whatsapp ? `WhatsApp: ${form.whatsapp}` : null,
      form.email ? `Email: ${form.email}` : null,
      form.city ? `City: ${form.city}` : null,
      form.travel_date ? `Travel date: ${form.travel_date}` : null,
      form.travellers ? `Travellers: ${form.travellers}` : null,
      sourceRef ? `Interested in: ${sourceRef}` : null,
      form.message ? `\nMessage: ${form.message}` : null,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        travellers: form.travellers ? Number(form.travellers) : undefined,
        source, source_ref: sourceRef,
      };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === undefined) && delete payload[k]);
      const { data } = await api.post("/leads", payload);
      toast.success(`Booking saved (Lead ${data.lead_code}). Opening WhatsApp…`);
      track("whatsapp_click", sourceRef || source);

      // Build WhatsApp URL with the booking details prefilled and redirect
      const waNumber = settings?.whatsapp_number || "+919866933180";
      const waUrl = buildWhatsappUrl(waNumber, buildWaMessage(data.lead_code));

      // Reset & close before navigating
      setOpen(false);
      setForm({ name: "", mobile: "", whatsapp: "", email: "", city: "", travel_date: "", travellers: "", message: "" });

      // Open WhatsApp in a new tab; fall back to same tab if the popup is blocked
      const win = window.open(waUrl, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = waUrl;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ? (
          children
        ) : (
          <Button className={triggerClassName} data-testid={triggerTestId}>Send Inquiry</Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Plan your journey</DialogTitle>
          <DialogDescription>Share a few details and a travel curator will reach out within 24 hours.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2">
            <Label htmlFor="inq-name">Name*</Label>
            <Input id="inq-name" data-testid="inq-name" value={form.name} onChange={onChange("name")} required />
          </div>
          <div>
            <Label htmlFor="inq-mobile">Mobile*</Label>
            <Input id="inq-mobile" data-testid="inq-mobile" value={form.mobile} onChange={onChange("mobile")} required />
          </div>
          <div>
            <Label htmlFor="inq-whatsapp">WhatsApp</Label>
            <Input id="inq-whatsapp" data-testid="inq-whatsapp" value={form.whatsapp} onChange={onChange("whatsapp")} />
          </div>
          <div>
            <Label htmlFor="inq-email">Email</Label>
            <Input id="inq-email" type="email" data-testid="inq-email" value={form.email} onChange={onChange("email")} />
          </div>
          <div>
            <Label htmlFor="inq-city">City</Label>
            <Input id="inq-city" data-testid="inq-city" value={form.city} onChange={onChange("city")} />
          </div>
          <div>
            <Label htmlFor="inq-date">Travel Date</Label>
            <Input id="inq-date" type="date" data-testid="inq-date" value={form.travel_date} onChange={onChange("travel_date")} />
          </div>
          <div>
            <Label htmlFor="inq-travellers">Travellers</Label>
            <Input id="inq-travellers" type="number" min="1" data-testid="inq-travellers" value={form.travellers} onChange={onChange("travellers")} />
          </div>
          <div className="col-span-2">
            <Label htmlFor="inq-message">Message</Label>
            <Textarea id="inq-message" rows={3} data-testid="inq-message" value={form.message} onChange={onChange("message")} />
          </div>
          <div className="col-span-2 flex justify-end mt-2">
            <Button type="submit" disabled={submitting} data-testid="inquiry-submit-btn" className="bg-brand-navy-800 hover:bg-brand-navy-700 text-white">
              {submitting ? "Saving…" : "Save & Continue on WhatsApp"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
